/**
 * @date: 2/28/20
 * @author: Yan Wang <ywang@max.md>
 * @copy: (C) Copyright 2020 MaxMD Corporation, All Rights Reserved.
 */


import React, {useEffect, useState} from "react";
import './Auth.css';

import {getQueryParams, handleHttpResponse, handleHttpResponseAcceptAuthError} from "../utils/http-helpers";
import clsx from "clsx";
import {getKeyName, printOrganizationAddressInOneLine} from "../utils/portal-helper";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import makeStyles from "@mui/styles/makeStyles";
import {blue, cyan, deepOrange, deepPurple, green, indigo, pink, yellow} from "@mui/material/colors";
import {
    Alert, AlertTitle,
    alpha,
    Avatar,
    Backdrop,
    Button,
    CircularProgress,
    Container,
    ListItem,
    ListItemIcon,
    ListItemText
} from "@mui/material";
import {List} from "@mui/icons-material";
import ShowMessage from "../utils/show-message";

const useStyles = makeStyles((theme) => ({
    formRoot: {
        width: '100%',
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '25ch',
        },
    },
    grow: {
        flexGrow: 1,
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    title: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginRight: theme.spacing(2),
        marginLeft: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto',
        },
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },
    sectionMobile: {
        display: 'flex',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 350,
    },
    formInput: {
        margin: theme.spacing(1),
        minWidth: '100%',
    },
    loginButton: {
        minHeight: 56,
        width: 300,
        // minWidth: '100%',
        fontSize: 17,
        fontWeight: 'bold',
        textTransform: 'none',
        marginBottom: 10
    },
    forgotPwdButton: {
        textTransform: 'none'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    testAlignLeft: {
        textAlign: 'left'
    },
    root: {
        display: 'flex',
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    small: {
        width: theme.spacing(3),
        height: theme.spacing(3),
    },
    large: {
        width: theme.spacing(10),
        height: theme.spacing(10),
    },
    deepOrange: {
        color: theme.palette.getContrastText(deepOrange[500]),
        backgroundColor: deepOrange[500],
    },
    blue: {
        color: theme.palette.getContrastText(blue[500]),
        backgroundColor: blue[500]
    },
    yellow: {
        color: theme.palette.getContrastText(yellow[700]),
        backgroundColor: yellow[700]
    },

    cyan: {
        color: theme.palette.getContrastText(cyan[900]),
        backgroundColor: cyan[900]
    },
    green: {
        color: theme.palette.getContrastText(green[900]),
        backgroundColor: green[900]
    },
    indigo: {
        color: theme.palette.getContrastText(indigo[500]),
        backgroundColor: indigo[500]
    },
    pink: {
        color: theme.palette.getContrastText(pink[500]),
        backgroundColor: pink[500]
    },
    deepPurple: {
        color: theme.palette.getContrastText(deepPurple[500]),
        backgroundColor: deepPurple[500],
    },

    menuItem: {
        minWidth: 300, marginBottom: 10
    },
    listItem: {
        paddingTop: 10, marginTop: 10, borderTop: 'solid 1px #eeeeee'
    },
    activeMenuItem: {
        backgroundColor: '#ddd'
    },
    menuItemText: {
        marginLeft: 10
    },
    chooseOrgButton: {
        marginLeft: 0, paddingLeft:0
    },
    avatarSmallText:{
        fontSize: '1.25rem'
    },
    avatarNormalText:{
        fontSize: '1.45rem'
    },
    avatarLarge: {
        width: theme.spacing(7),
        height: theme.spacing(7),
    },
    avatar: {
        marginRight: 10
    }

}));

export default function ChooseOrganizationWindow(props) {
    const handleBasicLoginCompleted = props.handleBasicLoginCompleted;
    const signOut = props.signOut;
    const account = props.account || {};
    const queryParams = getQueryParams();
    const classes = useStyles();

    const [msg, setMsg] = useState({});
    const [loading, setLoading] = useState(null);

    var firstName = account.users && account.users.length > 0 && account.users[0].firstName || null;
    const getAvatarClassName = (nameKey, index) => {
        const colorList = [
            classes.deepOrange, classes.blue, classes.yellow, classes.cyan, classes.green, classes.indigo, classes.pink, classes.deepPurple
        ];
        const colorCls = colorList [ index % colorList.length ];
        // let colorCls;
        // switch (index){
        //     case 0: colorCls = classes.deepOrange ; break;
        //     case 1: colorCls = classes.blue ; break;
        //     case 2: colorCls = classes.yellow ; break;
        //     case 3: colorCls = classes.cyan ; break;
        //     case 4: colorCls = classes.green ; break;
        //     case 5: colorCls = classes.indigo ; break;
        //     case 6: colorCls = classes.pink ; break;
        //     case 7: colorCls = classes.deepPurple ; break;
        // }
        let sizeCls;
        if (nameKey.length > 2){
            sizeCls = classes.avatarSmallText
        } else {
            sizeCls = classes.avatarNormalText;
        }
        return clsx(colorCls, sizeCls, classes.avatarLarge, classes.avatar);
    }

    const submitSelectOrgForm = (org) => {
        setLoading(`Entering ${org.name}`);
        // const url = `/api/account/switch/organization/${org.id}`;
        const url = ApiUrl.Organization.switchTo.getPath(org.id);
        const requestOption = {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: ''
        };
        console.log(`${requestOption.method} ${url}`);

        fetch(url, requestOption)
        .then(handleHttpResponseAcceptAuthError)
        .catch( (err) => {
            console.error('Select Organization failed: ', err);
            setLoading(null);
            if (err){
                setMsg({error: `Failed to select the organization: ${err}`});
            }
            return false;
        }).then ( (res) => {
            if (res){
                console.log(res);
                var gotoUrl = window.location.href;
                var p = gotoUrl.indexOf("#");
                if (p){
                    gotoUrl = gotoUrl.substr(0, p);
                }
                window.location.href = gotoUrl;
                return ;
            }
        });
    }
    const handleSelectOrg = (org) => {

        if (!loading){
            submitSelectOrgForm(org);
        }
    }



    var authorizeUrl = `${PortalUrl.SignInWithIdme}?url=`;
    // var authorizeUrl = "/auth/signIn?url=";
    if (queryParams.error && window.location.pathname == "/"){
        authorizeUrl += encodeURIComponent(queryParams.url || "/");
    } else {
        authorizeUrl += encodeURIComponent(window.location.pathname + window.location.search);
    }
    // const authorizeUrl = "/auth/signIn?url=" + encodeURIComponent(window.location.pathname + window.location.search);

    return (
        <div>
            <Container className='auth-container' maxWidth='xs' >
                <div className='logo-wrapper'>
                    <img src={require('../images/dt-logo.png')?.default}/>
                </div>
                {/*<br/>*/}
                {/*<img src={require('../images/dt-logo.png')}/>*/}
                {/*<br/><br/>*/}
                <h3 className='auth-title'>Choose an Organization</h3>

                <Alert color='info' icon={false} className={classes.testAlignLeft}>
                    {
                        firstName && `Welcome ${firstName}. `
                    }
                    We found multiple organizations in your account, please choose an organization to enter the portal.
                </Alert>

                {queryParams && queryParams.error &&
                <Alert severity="warning" className={classes.testAlignLeft}>
                    <AlertTitle> <strong>{queryParams.error} {queryParams.errorTitle || ""}</strong> </AlertTitle>
                    {queryParams.errorDescription || ""}
                </Alert>
                }
                <ShowMessage msg={msg} />


                <List>
                    {account.organizations.map( (org, index) => {
                            var key = getKeyName(org.name);
                            var cls = getAvatarClassName(key, index);
                            const listItemCls = index > 0 ? classes.listItem : '';

                            return <ListItem key={`org-menu-item-${index}`} className={listItemCls} button onClick={() => handleSelectOrg(org)}>
                                <ListItemIcon>
                                    <Avatar style={{marginRight: 17}} className={cls} >{key}</Avatar>
                                </ListItemIcon>
                                <ListItemText primary={org.name} secondary={printOrganizationAddressInOneLine(org)} />
                            </ListItem>
                        }
                    )}
                </List>


                <div className='or-bar' >
                </div>

                <Button className={classes.loginButton} size='large' variant='outlined' color='primary' onClick={signOut} >
                    Sign Out
                </Button>


            </Container>
            <Backdrop className={classes.backdrop} open={Boolean(loading)} >
                <CircularProgress color={"inherit"}/> {loading}
            </Backdrop>

        </div>

    );
}

